export const INTELLIGENT_UI_ROUTE = "guides/chatgpt-intelligent-ui";
export const INTELLIGENT_UI_SOURCE = "https://openai.com/index/gpt-6-for-everyone/";
export const INTELLIGENT_UI_CHECKED = "2026-10-10";

export const intelligentUIStory = [
  {
    title: "Ask",
    description: "Start with a question.",
    image: "ask",
    alt: "Anchor Sprint's blue robot considers a question beside an open notebook and travel pictures.",
  },
  {
    title: "Explore",
    description: "See the answer come to life.",
    image: "explore",
    alt: "Anchor Sprint's blue robot presents a miniature Malaysian travel map, picture postcards, a chart and an illustrated plan.",
  },
  {
    title: "Adjust",
    description: "Make it your own.",
    image: "adjust",
    alt: "Anchor Sprint's blue robot adjusts a blue control beside a visual map and a personalised plan.",
  },
];

export const intelligentUIFaqs = [
  {
    question: "What is ChatGPT Intelligent UI?",
    answer: "It is ChatGPT's ability to shape an answer with text, visuals and interactive controls. The format depends on the task: a comparison, a diagram or a small tool can make an answer easier to explore.",
  },
  {
    question: "How do I try Intelligent UI in ChatGPT?",
    answer: "Describe your task and ask for an interactive answer, such as a plan with adjustable inputs. Copy an example prompt below, open ChatGPT and paste it into a new chat. The result can vary with the task, your account and the available features.",
  },
  {
    question: "Are the examples on this page connected to ChatGPT?",
    answer: "These are interactive illustrations built by Anchor Sprint with sample data. They run in your browser. The Open ChatGPT link takes you to ChatGPT; it does not automatically transfer a prompt, conversation or company data.",
  },
  {
    question: "Does Intelligent UI replace an AI agent?",
    answer: "An interactive answer helps you explore information. An agent that reads or changes your business systems also needs tools, permissions, integrations and review. A useful visual interface by itself does not grant access to those systems.",
  },
  {
    question: "Can my team use it for business work?",
    answer: "Start with a small task, such as planning a workshop or comparing options, and check the result. Use your organisation's approved ChatGPT workspace and data rules. Anchor Sprint offers ChatGPT training and rollout support for teams in Malaysia and Singapore.",
  },
];

export const intelligentUIBenefits = [
  { title: "See it", body: "A picture, timeline or comparison can make an unfamiliar idea easier to understand." },
  { title: "Change it", body: "Adjust an input and explore how the answer responds, without starting again." },
  { title: "Use it", body: "Turn the answer into a plan, a checklist or a decision you can discuss with your team." },
];

export type PenangDuration = 3 | 6;
export type PenangStopId = "food" | "heritage" | "reflection" | "waterfront";

export const penangStops: { id: PenangStopId; title: string; description: string; detail: string; alt: string; pin: [number, number] }[] = [
  { id: "food", title: "Local food", description: "Kick off with Penang flavours.", detail: "Meet over a relaxed meal and agree on the day’s goal. Confirm dietary needs with your team before choosing a venue.", alt: "Illustrative plate of Penang-style noodles with prawns.", pin: [22, 29] },
  { id: "heritage", title: "Heritage walk", description: "Explore historic streets together.", detail: "Use the walk to notice something new and exchange ideas. Choose an accessible route and check the weather before heading out.", alt: "Illustrative colourful heritage shophouses beneath a leafy tree.", pin: [43, 58] },
  { id: "reflection", title: "Team reflection", description: "Pause to share ideas over coffee.", detail: "The longer plan makes room for a team discussion. Each person shares one observation and one idea to take back to work.", alt: "Illustrative outdoor café table with coffee, a notebook and pens.", pin: [56, 68] },
  { id: "waterfront", title: "Waterfront", description: "Wrap up by the water and enjoy the views.", detail: "Finish with a short team check-in and decide on one next step. This illustration does not verify access, opening hours or venue bookings.", alt: "Illustrative Penang-style waterfront promenade, blue sea and distant hills.", pin: [70, 81] },
];

/** Illustrative slots include travel. The three-hour plan removes reflection. */
export function getPenangPlan(duration: PenangDuration) {
  const slots = duration === 3 ? [45, 75, 60] : [90, 90, 90, 90];
  const stops = duration === 3 ? penangStops.filter(({ id }) => id !== "reflection") : penangStops;
  let minute = 9 * 60;
  const time = (value: number) => `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
  return {
    stops: stops.map((stop, index) => {
      const start = time(minute);
      minute += slots[index];
      return { ...stop, time: start, minutes: slots[index] };
    }),
    finish: time(9 * 60 + duration * 60),
  };
}
